import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  Check, 
  X, 
  Clock, 
  Mail,
  AlertCircle,
  User,
  Calendar,
  MessageSquare,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getPendingInvites, acceptInvite, rejectInvite } from '../services/api';

const Notifications = ({ onNotificationCountChange }) => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.id) {
      loadInvites();
    }
  }, [user]);

  const loadInvites = async () => {
    try {
      setLoading(true);
      const data = await getPendingInvites(user.id);
      setInvites(data);
    } catch (error) {
      console.error('Error loading invites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    try {
      setProcessing(prev => ({ ...prev, [inviteId]: 'accepting' }));
      
      await acceptInvite(inviteId, user.id);

      // Remove accepted invite from list
      setInvites(prev => prev.filter(invite => invite.id !== inviteId));
      
      // Trigger notification count refresh in header
      if (onNotificationCountChange) {
        onNotificationCountChange();
      }
      
      // Show success message (you could add a toast here)
      alert('Collaboration invite accepted successfully!');
      
    } catch (error) {
      console.error('Error accepting invite:', error);
      alert(error.message || 'Failed to accept invite');
    } finally {
      setProcessing(prev => ({ ...prev, [inviteId]: null }));
    }
  };

  const handleRejectInvite = async (inviteId) => {
    try {
      setProcessing(prev => ({ ...prev, [inviteId]: 'rejecting' }));
      
      await rejectInvite(inviteId, user.id);

      // Remove rejected invite from list
      setInvites(prev => prev.filter(invite => invite.id !== inviteId));
      
      // Trigger notification count refresh in header
      if (onNotificationCountChange) {
        onNotificationCountChange();
      }
      
      // Show success message
      alert('Collaboration invite rejected.');
      
    } catch (error) {
      console.error('Error rejecting invite:', error);
      alert(error.message || 'Failed to reject invite');
    } finally {
      setProcessing(prev => ({ ...prev, [inviteId]: null }));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'collaboration':
        return <User className="h-4 w-4 text-gray-600" />;
      case 'evaluation':
        return <Check className="h-4 w-4 text-celadon-600" />;
      case 'system':
        return <MessageSquare className="h-4 w-4 text-gray-600" />;
      default:
        return <MessageSquare className="h-4 w-4 text-gray-500" />;
    }
  };

  const getNotificationBadgeClass = (type) => {
    switch (type) {
      case 'collaboration':
        return 'badge-status-info';
      case 'evaluation':
        return 'badge-status-success';
      case 'system':
        return 'badge-status-info';
      default:
        return 'badge-status-info';
    }
  };

  const getPermissionIcon = (level) => {
    switch (level) {
      case 'owner':
        return <User className="h-4 w-4 text-celadon-600" />;
      case 'editor':
        return <Check className="h-4 w-4 text-celadon-600" />;
      case 'reviewer':
        return <MessageSquare className="h-4 w-4 text-gray-600" />;
      case 'commenter':
        return <Mail className="h-4 w-4 text-gray-600" />;
      default:
        return <User className="h-4 w-4 text-gray-400" />;
    }
  };

  const getPermissionColor = (level) => {
    switch (level) {
      case 'owner':
        return 'bg-celadon-100 text-celadon-800 border-celadon-200';
      case 'editor':
        return 'bg-celadon-100 text-celadon-800 border-celadon-200';
      case 'reviewer':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'commenter':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-500 border-gray-200';
    }
  };

  const isExpired = (expiresAt) => {
    return new Date() > new Date(expiresAt);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <Bell className="h-8 w-8 text-primary-600" />
            <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
          </div>
          <p className="text-gray-600">
            Manage your collaboration invitations and stay updated on your projects.
          </p>
        </div>

        {/* Collaboration Invites Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <User className="h-5 w-5 text-primary-600" />
                <h2 className="text-lg font-semibold text-gray-900">Collaboration Invitations</h2>
                {invites.length > 0 && (
                  <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded-full text-sm font-medium">
                    {invites.length}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {invites.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No pending invitations</h3>
                <p className="text-gray-500">
                  You're all caught up! When someone invites you to collaborate on a prompt, it will appear here.
                </p>
              </div>
            ) : (
              invites.map((invite) => (
                <InviteCard
                  key={invite.id}
                  invite={invite}
                  processing={processing[invite.id]}
                  onAccept={() => handleAcceptInvite(invite.id)}
                  onReject={() => handleRejectInvite(invite.id)}
                  formatDate={formatDate}
                  getPermissionIcon={getPermissionIcon}
                  getPermissionColor={getPermissionColor}
                  isExpired={isExpired}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const InviteCard = ({ 
  invite, 
  processing, 
  onAccept, 
  onReject, 
  formatDate, 
  getPermissionIcon, 
  getPermissionColor,
  isExpired 
}) => {
  const expired = isExpired(invite.expires_at);
  
  return (
    <div className={`p-6 ${expired ? 'bg-gray-50 opacity-75' : 'bg-white'}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <h3 className="text-lg font-medium text-gray-900">
              {invite.prompt_name}
            </h3>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPermissionColor(invite.permission_level)}`}>
              {getPermissionIcon(invite.permission_level)}
              <span className="ml-1 capitalize">{invite.permission_level}</span>
            </span>
            {expired && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                <Clock className="h-3 w-3 mr-1" />
                Expired
              </span>
            )}
          </div>
          
          <p className="text-sm text-gray-600 mb-3">
            <span className="font-medium">{invite.inviter_name}</span> invited you to collaborate on this prompt in project <span className="font-medium">{invite.project_name}</span>
          </p>

          {invite.message && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
              <p className="text-sm text-gray-700 italic">"{invite.message}"</p>
            </div>
          )}

          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <div className="flex items-center space-x-1">
              <Calendar className="h-3 w-3" />
              <span>Invited {formatDate(invite.created_at)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Clock className="h-3 w-3" />
              <span>Expires {formatDate(invite.expires_at)}</span>
            </div>
          </div>
        </div>

        {!expired && (
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={onReject}
              disabled={processing}
              className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {processing === 'rejecting' ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
              ) : (
                <X className="h-4 w-4 mr-2" />
              )}
              Reject
            </button>
            
            <button
              onClick={onAccept}
              disabled={processing}
              className="flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {processing === 'accepting' ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Accept
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications; 